/**
 * HR Leave History Import page — /hr/leave-history-import
 *
 * Four-step guided wizard, mirroring the leave-balance import:
 *   1. Prepare — plain-language guidance + column guide + template
 *   2. Upload  — file picker, parse + dry-run
 *   3. Preview — per-row verification table + balance reconciliation + skip
 *   4. Done    — success confirmation
 *
 * History is the source of truth for usage: after the records land, each
 * employee+type+year balance bucket's used (approved days) and pending
 * (pending days) is overwritten from the file. Entitlement is owned by Import
 * balances, so HR runs that first.
 */

import { useState } from 'react'
import { Card } from '../components/primitives/Card'
import { PageHead } from '../components/primitives/PageHead'
import { FadeIn } from '../components/primitives/FadeIn'
import { HrAccessGuard } from '../components/hr/HrAccessGuard'
import {
  PrepareStep,
  UploadStep,
  PreviewStep,
  BalancesStep,
  DoneStep,
} from './hr-leave-history/HistoryImportSteps'
import type { HistoryImportResult, HistoryImportRow } from '../api/hr-leave-history'

type Step = 'prepare' | 'upload' | 'preview' | 'balances' | 'done'

const STEP_ORDER: Step[] = ['prepare', 'upload', 'preview', 'balances', 'done']

const STEP_LABELS: Record<Step, string> = {
  prepare: 'Prepare your file',
  upload: 'Upload',
  preview: 'Check the records',
  balances: 'Review balances',
  done: 'Done',
}

// ── Step indicator ────────────────────────────────────────────────────────────

function StepIndicator({ current }: { current: Step }): JSX.Element {
  const currentIdx = STEP_ORDER.indexOf(current)
  return (
    <div
      style={{
        display: 'flex',
        gap: 20,
        marginBottom: 24,
        padding: '12px 16px',
        background: 'var(--surface-2)',
        borderRadius: 8,
        flexWrap: 'wrap',
      }}
    >
      {STEP_ORDER.map((key, i) => {
        const done = i < currentIdx
        const active = key === current
        return (
          <div key={key} aria-current={active ? 'step' : undefined} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 24,
                height: 24,
                borderRadius: '50%',
                fontSize: 12,
                fontWeight: 700,
                background: done ? 'var(--success-fg)' : active ? 'var(--steel-blue)' : 'var(--surface-2)',
                color: done || active ? 'var(--surface)' : 'var(--text-3)',
                border: active || done ? 'none' : '1px solid var(--border)',
                flexShrink: 0,
              }}
            >
              {done ? '✓' : i + 1}
            </span>
            <span style={{ fontSize: 13, fontWeight: active ? 600 : 400, color: active ? 'var(--text)' : 'var(--text-3)' }}>
              {STEP_LABELS[key]}
            </span>
          </div>
        )
      })}
    </div>
  )
}

// ── Page inner ────────────────────────────────────────────────────────────────

function HrLeaveHistoryImportInner(): JSX.Element {
  const [step, setStep] = useState<Step>('prepare')
  const [parsedRows, setParsedRows] = useState<HistoryImportRow[]>([])
  const [previewResult, setPreviewResult] = useState<HistoryImportResult | null>(null)
  // Row indices HR has chosen to skip — lifted here so they survive moving
  // between the records step and the balances step.
  const [skipped, setSkipped] = useState<ReadonlySet<number>>(new Set())
  // Dry-run computed on exactly the kept rows + the kept rows themselves,
  // carried into the balances step and the final commit.
  const [balancesResult, setBalancesResult] = useState<HistoryImportResult | null>(null)
  const [keptRows, setKeptRows] = useState<HistoryImportRow[]>([])
  const [finalResult, setFinalResult] = useState<HistoryImportResult | null>(null)

  function toggleSkip(rowIndex: number): void {
    setSkipped((prev) => {
      const next = new Set(prev)
      if (next.has(rowIndex)) next.delete(rowIndex)
      else next.add(rowIndex)
      return next
    })
  }

  function handlePreviewReady(rows: HistoryImportRow[], result: HistoryImportResult): void {
    setParsedRows(rows)
    setPreviewResult(result)
    setSkipped(new Set())
    setStep('preview')
  }

  function handleReset(): void {
    setParsedRows([])
    setPreviewResult(null)
    setSkipped(new Set())
    setBalancesResult(null)
    setKeptRows([])
    setFinalResult(null)
    setStep('prepare')
  }

  return (
    <>
      <PageHead
        eyebrow="HR"
        title="Import leave history"
        subtitle="Bring past leave records from the old system into Leave Hub. Used and pending balances are recalculated from them. Preview before committing."
      />

      <StepIndicator current={step} />

      <Card padding={24}>
        <FadeIn triggerKey={step} rise={4} duration="fast">
          {step === 'prepare' ? (
            <PrepareStep onNext={() => setStep('upload')} />
          ) : step === 'upload' ? (
            <UploadStep onPreviewReady={handlePreviewReady} onBack={() => setStep('prepare')} />
          ) : step === 'preview' && previewResult != null ? (
            <PreviewStep
              rows={parsedRows}
              preview={previewResult}
              skipped={skipped}
              onToggleSkip={toggleSkip}
              onBack={() => setStep('upload')}
              onNext={(balancesPreview, kept) => {
                setBalancesResult(balancesPreview)
                setKeptRows(kept)
                setStep('balances')
              }}
            />
          ) : step === 'balances' && balancesResult != null ? (
            <BalancesStep
              preview={balancesResult}
              keptRows={keptRows}
              onBack={() => setStep('preview')}
              onCommit={(result) => {
                setFinalResult(result)
                setStep('done')
              }}
            />
          ) : step === 'done' && finalResult != null ? (
            <DoneStep preview={finalResult} onReset={handleReset} />
          ) : null}
        </FadeIn>
      </Card>
    </>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function HrLeaveHistoryImportPage(): JSX.Element {
  return (
    <HrAccessGuard>
      <HrLeaveHistoryImportInner />
    </HrAccessGuard>
  )
}
